import { logMessage } from '../sentry';
import { AdUnitEventListener } from './vpaid-ad-unit-event-listener';
import {
  VPAIDAdUnitV2,
  AdUnitVersion,
  AdUnitEventName
} from '../../../@types/vpaid.type';

interface ExpectedEvent {
  expectedEvent: AdUnitEventName;
  watchOnlyIf?: () => boolean;
}

interface WatchParameters {
  threshold: number;
  adUnitVersion: AdUnitVersion;
  onErrorCallback: (eventName: AdUnitEventName) => void;
  thresholdByEventName?: {
    [eventName: string]: number;
  };
}

export class AdUnitMethodWatcher {
  _expectedEventsOnAdUnitCall: {
    [vpaidMethodName: string]: ExpectedEvent;
  } = {
    initAd: {
      expectedEvent: 'AdLoaded'
    },
    startAd: {
      expectedEvent: 'AdStarted'
    },
    stopAd: {
      expectedEvent: 'AdStopped'
    },
    skipAd: {
      expectedEvent: 'AdSkipped',
      watchOnlyIf: () => {
        return this._adUnitVersion === 2;
      }
    },
    resizeAd: {
      expectedEvent: 'AdSizeChange',
      watchOnlyIf: () => {
        return this._adUnitVersion === 2;
      }
    },
    expandAd: {
      expectedEvent: 'AdExpandedChange'
    },
    collapseAd: {
      expectedEvent: 'AdExpandedChange'
    },
    pauseAd: {
      expectedEvent: 'AdPaused'
    },
    resumeAd: {
      expectedEvent: 'AdPlaying'
    }
  };

  _awaitedVPAIDEvent: {
    [eventName: string]: {
      timeoutHandle: number;
    };
  } = {};

  _adUnitVersion!: AdUnitVersion;
  _adUnit!: VPAIDAdUnitV2;
  _eventListener: AdUnitEventListener;

  constructor(eventListener: AdUnitEventListener) {
    this._eventListener = eventListener;
  }

  watchAdUnitResponse(
    adUnit: VPAIDAdUnitV2,
    parameters: WatchParameters
  ): void {
    this._adUnit = adUnit;

    const areParametersEmpty: boolean =
      !parameters ||
      !parameters.threshold ||
      !parameters.onErrorCallback ||
      !parameters.adUnitVersion;

    if (areParametersEmpty) {
      logMessage(
        'Invalid parameters for VPAID overload. Skipping event security check.'
      );
      return;
    }

    this._adUnitVersion = parameters.adUnitVersion;

    for (const method in this._expectedEventsOnAdUnitCall) {
      if (
        this._expectedEventsOnAdUnitCall[method].watchOnlyIf &&
        !this._expectedEventsOnAdUnitCall[method].watchOnlyIf?.()
      ) {
        continue;
      }

      const errorCallback = ((eventName: AdUnitEventName) => {
        parameters.onErrorCallback(eventName);
      }).bind(this);

      const expectedEvent = this._expectedEventsOnAdUnitCall[method]
        .expectedEvent;
      const threshold: number =
        parameters.thresholdByEventName &&
        parameters.thresholdByEventName[expectedEvent]
          ? parameters.thresholdByEventName[expectedEvent]
          : parameters.threshold;

      this._watchEventReturn(expectedEvent, method, errorCallback, threshold);
    }
  }

  _watchEventReturn(
    eventName: AdUnitEventName,
    methodName: string,
    onThresholdCallback: (eventName: AdUnitEventName) => void,
    threshold: number = 1000
  ): void {
    const initTimeoutHandle = () => {
      const timeoutHandle = setTimeout(() => {
        delete this._awaitedVPAIDEvent[eventName];
        onThresholdCallback(eventName);
      }, threshold) as any;

      this._awaitedVPAIDEvent[eventName] = {
        timeoutHandle: timeoutHandle
      };
    };

    const clearTimeoutHandle = () => {
      if (this._awaitedVPAIDEvent[eventName]) {
        clearTimeout(this._awaitedVPAIDEvent[eventName].timeoutHandle);
      }
      delete this._awaitedVPAIDEvent[eventName];
    };

    const originalMethodPointer = (this._adUnit as any)[methodName];

    if (!originalMethodPointer) {
      logMessage(
        `Method ${methodName} not found in VPAID ad unit version ${this._adUnitVersion}`
      );
      return;
    }

    const self = this;
    (this._adUnit as any)[methodName] = function(...args: any[]) {
      clearTimeoutHandle();
      initTimeoutHandle();
      originalMethodPointer.apply(self._adUnit, args);
    };

    this._eventListener.addEventListener(eventName, () => {
      clearTimeoutHandle();
    });
  }
}
